import { FC } from 'react';

import BoltIcon from '@/assets/bolt.svg?react';
import { FormControl, FormField, FormItem } from '@/components/ui/Form';
import TransmissionLineTypesModal from '@/features/v2/hh/components/TransmissionLineTypesModal/TransmissionLineTypesModal';

import { LINE_TYPE, type LineType } from '../schema';
import PlotOptionCard from './layout/PlotOptionCard';
import SectionHeader from './SectionHeader';
import { useLingui } from '@lingui/react';
import { useFormContext } from 'react-hook-form';

export const LINE_TYPES_NAME = 'line_types';

interface Props {
  name?: string;
}

const CheckboxGroupLineTypes: FC<Props> = ({ name = LINE_TYPES_NAME }) => {
  const { i18n } = useLingui();
  const { control, trigger } = useFormContext();

  const lineTypeOptions: { value: LineType; label: string }[] = [
    { value: LINE_TYPE.LOW, label: i18n.t('Niskie napięcie') },
    { value: LINE_TYPE.MEDIUM, label: i18n.t('Średnie napięcie') },
    { value: LINE_TYPE.HIGH, label: i18n.t('Wysokie napięcie') },
    { value: LINE_TYPE.TRANSMISSION, label: i18n.t('Linia przesyłowa') },
    { value: LINE_TYPE.TRANSFORMER, label: i18n.t('Słup z transformatorem') },
  ];

  return (
    <FormField
      control={control}
      name={name}
      rules={{
        validate: (value) =>
          (value as LineType[]).length > 0 ||
          i18n.t('Wybierz co najmniej jeden rodzaj linii'),
      }}
      render={({ field: { value, onChange }, fieldState: { error } }) => {
        const selected = value as LineType[];
        const toggle = (v: LineType) => {
          if (selected.includes(v)) {
            onChange(selected.filter((x) => x !== v));
          } else {
            onChange([...selected, v]);
          }
          trigger(name);
        };
        return (
          <FormItem className="rounded-md bg-gray-200 p-[14px] md:p-6">
            <SectionHeader
              icon={BoltIcon}
              label={i18n.t('Jaki rodzaj linii znajduje się na działce?')}
            />
            <FormControl>
              <div className="flex flex-col gap-2 md:gap-3">
                <div className="grid grid-cols-1 gap-2 md:gap-3 lg:grid-cols-2">
                  {lineTypeOptions.map((opt) => (
                    <PlotOptionCard
                      key={opt.value}
                      label={opt.label}
                      checked={selected.includes(opt.value)}
                      type="checkbox"
                      onClick={() => toggle(opt.value)}
                    />
                  ))}
                </div>
                <TransmissionLineTypesModal variant="full" />
                {error && (
                  <p className="caption-large text-red-400">{error.message}</p>
                )}
              </div>
            </FormControl>
          </FormItem>
        );
      }}
    />
  );
};

export default CheckboxGroupLineTypes;
